import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  Home as HomeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  VideoFile as VideoFileIcon,
} from '@mui/icons-material';
import { hasWritePermission } from '../../../utils/menuFilter';


// Amenity color palette
const amenityColors = [
  { bg: '#E3F2FD', color: '#1565C0' },
  { bg: '#E8F5E9', color: '#2E7D32' },
  { bg: '#FFF3E0', color: '#EF6C00' },
  { bg: '#F3E5F5', color: '#7B1FA2' },
  { bg: '#E0F7FA', color: '#00838F' },
  { bg: '#FBE9E7', color: '#D84315' },
  { bg: '#E8EAF6', color: '#3949AB' },
  { bg: '#F1F8E9', color: '#558B2F' },
];

const ProjectDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const project = location.state?.project;

  const hasObjectAddress =
    project && project.address && typeof project.address === 'object';
  const addressObj = hasObjectAddress ? project.address : null;
  const fullAddress = hasObjectAddress
    ? addressObj?.fullAddress || ''
    : project?.address || '';
  const city = addressObj?.city || project?.city || '';
  const state = addressObj?.state || project?.state || '';
  const projectDocuments = Array.isArray(project?.documents) ? project.documents : [];
  const canWriteProject = hasWritePermission('project.project-list') || hasWritePermission('project');

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Project not found</Typography>
        <Button onClick={() => history.push('/app/project/ProjectList')}>
          Back to List
        </Button>
      </Box>
    );
  }


  const handleEdit = () => {
    history.push({
      pathname: '/app/project/edit',
      state: { project: project }
    });
  };


  const handleBack = () => {
    history.push('/app/project/ProjectList');
  };


  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {project.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Project No: {project.projectId}
              </Typography>
            </Box>
          </Box>
          {canWriteProject && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ textTransform: 'none', px: 3 }}
            >
              Edit Project
            </Button>
          )}
        </Box>
      </Box>


      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Badge Type
              </Typography>
              <Chip
                label={project.badge}
                sx={{
                  mt: 1,
                  backgroundColor: project.badgeColor || '#FFC107',
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={project.status}
                color={project.status === 'Active' ? 'success' : 'default'}
                sx={{ mt: 1, fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Project Type
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                {project.type}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Commission
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, color: '#536DFE' }}>
                {project.commission}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Main Details */}
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <HomeIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Basic Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Project ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.projectId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Project Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Developer
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.developer}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.type}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>


        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PhoneIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Contact Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Contact Person
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.contactPerson}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {project.email}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {project.phone}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>


        {/* Project Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Project Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.startDate?.split('|')[0] || 'TBD'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Completion Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.completeDate?.split('|')[0] || 'TBD'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  RERA Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.reraNo}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Total Plots
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.totalPlot}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Circle Rate
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.guidelineValue}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Possession Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.possessionDate}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>


        {/* Address Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <LocationIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Address Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Full Address
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {fullAddress || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  City
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {city || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  State
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {state || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>


        {/* Bank Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <BankIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Bank Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Bank Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.bankName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Account Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.accountNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  IFSC Code
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.ifscCode}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>


        {/* Amenities */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Amenities
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {(() => {
                // Parse amenities - handle array, string, or empty
                let amenitiesList = [];
                if (Array.isArray(project.amenities)) {
                  amenitiesList = project.amenities;
                } else if (typeof project.amenities === 'string' && project.amenities.trim()) {
                  amenitiesList = project.amenities.split(',').map(a => a.trim()).filter(a => a);
                }
                
                if (amenitiesList.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      No amenities listed
                    </Typography>
                  );
                }
                
                return amenitiesList.map((amenity, index) => {
                  const colorScheme = amenityColors[index % amenityColors.length];
                  return (
                    <Chip
                      key={index}
                      label={amenity}
                      sx={{
                        backgroundColor: colorScheme.bg,
                        color: colorScheme.color,
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        py: 2,
                        px: 1,
                      }}
                    />
                  );
                });
              })()}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Project Documents
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {projectDocuments.length > 0 ? (
              <Grid container spacing={2}>
                {projectDocuments.map((doc, index) => (
                  <Grid item xs={12} md={6} key={doc.id || doc._id || `${doc.title || 'document'}-${index}`}>
                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {doc.type === 'image'
                          ? <ImageIcon fontSize="small" color="primary" />
                          : doc.type === 'video'
                            ? <VideoFileIcon fontSize="small" color="primary" />
                            : <DescriptionIcon fontSize="small" color="primary" />}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {doc.title || 'Untitled Document'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Type: {doc.type || 'document'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Date: {doc.date || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Description: {doc.description || '-'}
                      </Typography>
                      {doc.fileViewUrl ? (
                        <Button
                          component="a"
                          href={doc.fileViewUrl}
                          target="_blank"
                          rel="noreferrer"
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none', alignSelf: 'flex-start', mt: 1 }}
                        >
                          {doc.fileName || 'Open File'}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {doc.fileName || 'No file attached'}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No project documents added yet.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};


export default ProjectDetails;
